import React, { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Typography from '../../../../../components/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CloseIcon from '@mui/icons-material/Close';
import HandymanIcon from '@mui/icons-material/Handyman';
import { LayoutContext } from '../../../../../Providers/LayoutProvider';
import { MESSAGE_TYPES, MESSAGE_SOURCES, getMessageText } from '../messageConfig';
import { getCanonicalGenptTargetUrl } from '../utils/getCanonicalGenptTargetUrl';
import { scenarioFieldsFromSavedRow } from '../utils/scenario';
import GreenCheck from '../../../../../components/SvgIcons/GreenCheck';
import TestSetup from './TestSetup';
import ThrottlingSetup from './ThrottlingSetup';
import ScheduleTest from './ScheduleTest';
import DeclarationModal from './DeclarationModal';

/**
 * TestConfigurationForm - Orchestrator that composes TestSetup, ThrottlingSetup, and Schedule
 * into a three-step flow: Configuration -> Throttling -> Schedule -> Declaration.
 *
 * @param {object|null} prefillTestConfig - When set (e.g. "Modify setup"), seeds submittedData from
 *   latest session; user stays on Configuration first (ROAR-4332).
 */
function TestConfigurationForm({
  msg = null,
  targetURL = null,
  testConfiguration = null,
  prefillTestConfig = null,
  chatId = null,
  sessionDetails = null,
  chatMessages = null,
  // Legacy props for backward compatibility (when used outside chat)
  open = true,
  setOpen = () => {},
  validateTargetData = null,
  handleStartTest = null,
  onConfigCompleted = null,
  /** When true, do not emit genpt:addMessage (e.g. Schedule GenPT modal outside chat). */
  suppressDeclarationEvent = false,
  /** When true (e.g. Schedule GenPT modal), omit outer card border/shadow — dialog already frames content. */
  embeddedInModal = false,
  isReadOnly: legacyIsReadOnly = false,
  submittedDataProp: legacySubmittedDataProp = null,
  /** When editing an existing schedule (Schedule GenPT modal): prefill test + throttle + schedule UI state */
  editPrefill = null,
  /** Schedule GenPT modal: closing declaration after “Yes, Cancel” discards the whole flow (closes parent dialog). */
  onDeclarationDiscard = null,
  /**
   * In modals: when false, schedule step shows "Schedule Test" (pick-asset-first new schedule, ROAR-4328).
   * When true, or whenever `embeddedInModal` is false (GenPT chat start-test wizard), label is "Confirm and Save".
   */
  scheduleSubmitConfirmAndSave = true,
  /** ROAR-4341 / edit: exclude this schedule id when checking start-time conflicts on the asset */
  editingScheduleId = null,
  /** When true (e.g. Schedule GenPT modal with `source="pipeline"`), omit the Schedule step; throttling goes straight to declaration / `onConfigCompleted`. */
  hideScheduleTab = false,
  /** CI/CD pipeline web: persist test + throttle from modal (runs before declaration / schedule POST). */
  onPipelineThrottleSave = null,
  /** When true with read-only mode, expand the Test Setup stepper immediately (e.g. Activities “View Setup Details” modal). */
  readOnlyDefaultExpanded = false,
  /** Read-only: match main wizard header (icon + title left, chevron right) instead of green check + expand icons. */
  readOnlyUseModalHeader = false,
  /** When set with `readOnlyUseModalHeader`, renders close (X) in the header row (e.g. Activities view-details modal). */
  onReadOnlyModalClose = null,
  /** When true, WordPress cred verify sends forScheduling on /genpt/validate_target (Schedule GenPT modal only). */
  forScheduling = false,
}) {
  const isReadOnly = msg ? msg?.status === 'completed' : legacyIsReadOnly;
  /** Same fall-through as APIConfigurationForm: synthetic `msg` (Schedule modal) has no `widget.data`. */
  const submittedDataProp = msg
    ? (msg?.widget?.data ?? testConfiguration ?? legacySubmittedDataProp ?? null)
    : legacySubmittedDataProp;
  const assetMode = msg?.metadata?.asset?.mode;
  const assetNetworkAccess = msg?.metadata?.asset?.network_access;
  const isInternalAsset =
    assetMode === 'internal' || assetNetworkAccess === 'internal';
  const assetType = msg?.metadata?.asset?.type || msg?.metadata?.asset?.asset_type;

  const { mainUrl } = useContext(LayoutContext);

  // Stepper state — prefill wins so "Modify setup" restores last session without re-submitting step 1
  const [activeStep, setActiveStep] = useState('configuration');

  useLayoutEffect(() => {
    if (hideScheduleTab && activeStep === 'schedule') {
      setActiveStep('throttling');
    }
  }, [hideScheduleTab, activeStep]);

  useLayoutEffect(() => {
    if (!embeddedInModal || activeStep !== 'schedule') return;
    const dialogContent = document.querySelector(
      '.MuiDialog-root.MuiModal-root .MuiDialogContent-root'
    );
    dialogContent?.scrollTo?.({ top: 0, behavior: 'auto' });
  }, [embeddedInModal, activeStep]);
  const [submittedData, setSubmittedData] = useState(
    () => editPrefill?.submittedData ?? prefillTestConfig ?? submittedDataProp ?? null
  );
  const [throttlingData, setThrottlingData] = useState(
    () => editPrefill?.throttlingData ?? null
  );

  // After history / "Modify setup", validation_response is often missing; infer WordPress from session or prefill
  // so TestSetup renders WordPressLegacyAuth (credentials + verify) instead of the generic web auth flow.
  const mergedValidateTargetData = useMemo(() => {
    const crmType = msg?.metadata?.validation_response?.crm_type;
    const normalizedCrmType = crmType ? String(crmType).toLowerCase() : null;
    const base =
      validateTargetData && typeof validateTargetData === 'object'
        ? { ...validateTargetData }
        : {};
    const result = { ...base };
    if (normalizedCrmType) result.crm_type = normalizedCrmType;

    const sessionTestType =
      msg?.metadata?.latest_session?.test_type ??
      msg?.metadata?.test_config_details?.test_type;
    const isSessionWordPress =
      sessionTestType != null &&
      String(sessionTestType).toLowerCase() === 'wordpress';

    const lsCrm = msg?.metadata?.latest_session?.crm_type;
    const isSessionCrmWordPress =
      lsCrm != null && String(lsCrm).toLowerCase() === 'wordpress';

    const dataTestTypes = [
      prefillTestConfig?.testType,
      submittedData?.testType,
      submittedDataProp?.testType,
      testConfiguration?.testType,
    ];
    const isDataWordPress = dataTestTypes.some(
      (t) => t != null && String(t).toLowerCase() === 'wordpress'
    );

    const isWordPressWidget =
      msg?.type === MESSAGE_TYPES.AUTH_WORDPRESS_VA ||
      msg?.type === MESSAGE_TYPES.UNAUTH_WORDPRESS_VA;

    /** Schedule modal / validate_target may send `test_type: wordpress` without `crm_type` (ROAR-4361). */
    const validateTargetTestType =
      validateTargetData?.test_type ?? validateTargetData?.testType;
    const isValidateTargetWordPress =
      validateTargetTestType != null &&
      String(validateTargetTestType).toLowerCase() === 'wordpress';

    if (
      !result.crm_type &&
      (isSessionWordPress ||
        isSessionCrmWordPress ||
        isDataWordPress ||
        isWordPressWidget ||
        isValidateTargetWordPress)
    ) {
      result.crm_type = 'wordpress';
    }

    return result;
  }, [
    msg?.metadata?.validation_response?.crm_type,
    msg?.metadata?.latest_session?.test_type,
    msg?.metadata?.latest_session?.crm_type,
    msg?.metadata?.test_config_details?.test_type,
    msg?.type,
    validateTargetData,
    validateTargetData?.test_type,
    validateTargetData?.testType,
    prefillTestConfig?.testType,
    submittedData?.testType,
    submittedDataProp?.testType,
    testConfiguration?.testType,
  ]);
  const [, setFormVisible] = useState(!isReadOnly);
  const [expanded, setExpanded] = useState(
    () =>
      Boolean(
        readOnlyDefaultExpanded &&
          (legacyIsReadOnly || (msg && String(msg.status).toLowerCase() === 'completed'))
      )
  );
  const [declarationSubmitted, setDeclarationSubmitted] = useState(false);
  const [declarationModalOpen, setDeclarationModalOpen] = useState(false);
  const [pendingModalFullConfig, setPendingModalFullConfig] = useState(null);
  const [declarationModalLoading, setDeclarationModalLoading] = useState(false);
  const [pipelineThrottleSaving, setPipelineThrottleSaving] = useState(false);

  /** Activities “View Setup Details” modal: stay expanded; do not run the chat read-only collapse. */
  const readOnlyAlwaysOpen = Boolean(readOnlyUseModalHeader && readOnlyDefaultExpanded);

  // When isReadOnly becomes true, collapse the inline chat widget (not the view-details modal).
  useEffect(() => {
    if (isReadOnly) {
      setFormVisible(false);
      if (readOnlyAlwaysOpen) {
        setExpanded(true);
      } else {
        setExpanded(false);
      }
    }
  }, [isReadOnly, readOnlyAlwaysOpen]);

  // When form is expanded after submission, switch to the last completed step (chat read-only only;
  // Activities “View Setup Details” stays on Configuration first.)
  const hasSwitchedStep = useRef(false);
  useEffect(() => {
    if (
      expanded &&
      submittedData?.config_settings &&
      !hasSwitchedStep.current &&
      !readOnlyAlwaysOpen
    ) {
      setActiveStep('throttling');
      hasSwitchedStep.current = true;
    }
    if (!expanded) hasSwitchedStep.current = false;
  }, [expanded, submittedData?.config_settings, readOnlyAlwaysOpen]);

  const finalTargetURL = useMemo(
    () =>
      getCanonicalGenptTargetUrl({
        targetURLProp: targetURL,
        msg,
        sessionDetails,
        messages: chatMessages,
      }),
    [targetURL, msg, sessionDetails, chatMessages]
  );

  // ── Compute initialValues for TestSetup from metadata ──
  const testSetupInitialValues = useMemo(() => {
    const latestSession = msg?.metadata?.latest_session;
    const testConfigDetails = msg?.metadata?.test_config_details;
    const sessionContinueUnauth =
      sessionDetails?.continue_as_unauth ?? sessionDetails?.continueAsUnauth;

    // Priority: local submittedData (wizard / declaration) > API metadata > props
    let source = null;

    const fromSubmitted = submittedData;
    if (
      fromSubmitted &&
      (fromSubmitted.testType != null ||
        fromSubmitted.authType != null ||
        fromSubmitted.showAuthFields !== undefined ||
        fromSubmitted.networkType != null)
    ) {
      source = {
        testType: fromSubmitted.testType,
        showAuthFields: fromSubmitted.showAuthFields,
        checked:
          fromSubmitted.checked !== undefined
            ? fromSubmitted.checked
            : fromSubmitted.showAuthFields,
        continueAsUnauth:
          fromSubmitted.continueAsUnauth ??
          fromSubmitted.continue_as_unauth ??
          sessionContinueUnauth,
        loginUrl: fromSubmitted.loginUrl,
        username: fromSubmitted.username,
        password: fromSubmitted.password,
        mfaSecretKey: fromSubmitted.mfaSecretKey,
        githubAccount: fromSubmitted.githubAccount,
        githubRepo: fromSubmitted.githubRepo,
        showGithubFields: !!(fromSubmitted.githubAccount || fromSubmitted.showGithubFields),
        vpnProfileId: fromSubmitted.selectedVpnProfile,
        savedCredentialId: fromSubmitted.savedCredential,
        savedCredentialName: fromSubmitted.savedCredentialName,
        networkType: fromSubmitted.networkType,
        // Business Scenario (ROAR-4840): round-trips the just-confirmed scenario back into
        // `TestSetup` so re-expanding the collapsed "Test Setup" summary within the same wizard
        // session shows the real confirmed name/description instead of "Scenario not yet confirmed".
        ...(fromSubmitted.testType === 'scenario' && {
          scenarioId: fromSubmitted.scenarioId,
          scenarioDescription: fromSubmitted.scenarioDescription,
          featureName: fromSubmitted.featureName,
          scenarioSteps: fromSubmitted.scenarioSteps,
        }),
      };
    } else if (testConfigDetails) {
      source = {
        testType: testConfigDetails.test_type,
        showAuthFields: testConfigDetails.authentication_type === 'authenticated',
        checked: testConfigDetails.authentication_type === 'authenticated',
        continueAsUnauth:
          testConfigDetails.continue_as_unauth ??
          testConfigDetails.continueAsUnauth ??
          sessionContinueUnauth,
        loginUrl: testConfigDetails.login_url,
        githubAccount: testConfigDetails.github?.accountName,
        githubRepo: testConfigDetails.github?.repoName,
        showGithubFields: !!testConfigDetails.github?.accountName,
        vpnProfileId: testConfigDetails.vpn_profile?.profile_id || testConfigDetails.vpn_profile,
        savedCredentialId: testConfigDetails.credential?.credential_id,
        savedCredentialName: testConfigDetails.credential?.credential_name,
        networkType: testConfigDetails.vpn_profile ? 'internal' : 'external',
      };
    } else if (latestSession) {
      /**
       * ROAR-4423: Activities → View Setup Details builds a synthetic msg whose only auth source
       * is `latest_session.credential`. The other branches above extract `savedCredentialName`
       * for the "Selected: …" pill; omitting it here forces the read-only form to wait on
       * `useFetchAssetCredentials` and silently fails when the credential was renamed/deleted
       * since the test ran. Carry the name through when the BE returned the full credential
       * object so the selected credential is shown immediately.
       */
      const credentialObj =
        latestSession.credential && typeof latestSession.credential === 'object'
          ? latestSession.credential
          : null;
      source = {
        testType: latestSession.test_type,
        showAuthFields: latestSession.authenticated_test === true,
        checked: latestSession.authenticated_test === true,
        continueAsUnauth:
          latestSession.continue_as_unauth ??
          latestSession.continueAsUnauth ??
          sessionContinueUnauth ??
          (latestSession.authenticated_test !== undefined
            ? !latestSession.authenticated_test
            : true),
        loginUrl: latestSession.authenticated_test === true ? latestSession.login_url : undefined,
        githubAccount: latestSession.github?.accountName,
        githubRepo: latestSession.github?.repoName,
        showGithubFields: !!latestSession.github?.accountName,
        vpnProfileId: latestSession.vpn_profile?.profile_id,
        savedCredentialId:
          credentialObj?.credential_id ||
          credentialObj?.id ||
          (credentialObj ? undefined : latestSession.credential),
        savedCredentialName:
          credentialObj?.credential_name || credentialObj?.name || undefined,
        networkType: latestSession.vpn_profile?.profile_id ? 'internal' : (isInternalAsset ? 'internal' : 'external'),
      };
    } else if (editPrefill?.submittedData) {
      const ds = editPrefill.submittedData;
      source = {
        testType: ds.testType,
        showAuthFields: ds.showAuthFields,
        checked: ds.checked,
        continueAsUnauth: ds.continueAsUnauth,
        loginUrl: ds.loginUrl,
        username: ds.username,
        password: ds.password,
        mfaSecretKey: ds.mfaSecretKey,
        githubAccount: ds.githubAccount,
        githubRepo: ds.githubRepo,
        showGithubFields: !!ds.githubAccount,
        vpnProfileId: ds.selectedVpnProfile,
        savedCredentialId: ds.savedCredential,
        savedCredentialName: ds.savedCredentialName,
        networkType: ds.networkType,
      };
    } else if (testConfiguration || submittedDataProp) {
      const ds = testConfiguration || submittedDataProp;
      source = {
        testType: ds.testType,
        showAuthFields: ds.showAuthFields,
        checked: ds.checked,
        continueAsUnauth: ds.continueAsUnauth,
        loginUrl: ds.loginUrl,
        username: ds.username,
        password: ds.password,
        mfaSecretKey: ds.mfaSecretKey,
        githubAccount: ds.githubAccount,
        githubRepo: ds.githubRepo,
        showGithubFields: !!ds.githubAccount,
        vpnProfileId: ds.selectedVpnProfile,
        savedCredentialId: ds.savedCredential,
        savedCredentialName: ds.savedCredentialName,
        networkType: ds.networkType,
      };
    }

    // View Setup Details (ROAR-4511): the synthetic message carries BOTH test_config_details and
    // latest_session, so the test_config_details branch above wins and sets test_type: 'scenario'
    // but no scenario fields — leaving the read-only Define Scenario section as "Scenario not yet
    // confirmed". Overlay the scenario fields regardless of which branch built `source`, sourced
    // from latest_session.scenario (always set by Activities' viewSetupSyntheticMsg) with
    // test_config_details.scenario as a fallback. Guarded on an empty featureName so the live wizard
    // (fromSubmitted, which already sets it) and non-scenario sessions are untouched.
    if (source && !source.featureName) {
      const scenarioSrc = latestSession?.scenario || testConfigDetails?.scenario;
      const isScenario =
        source.testType === 'scenario' ||
        latestSession?.test_type === 'scenario' ||
        testConfigDetails?.test_type === 'scenario';
      if (isScenario && scenarioSrc) {
        // ROAR-4511: shared row→fields mapping (also used by the CI/CD picker + restore merges).
        const fields = scenarioFieldsFromSavedRow(scenarioSrc);
        source.scenarioId = fields.scenarioId;
        source.scenarioDescription = fields.scenarioDescription;
        source.featureName = fields.featureName;
        source.scenarioSteps = fields.scenarioSteps;
      }
    }

    return source || {};
  }, [
    submittedData,
    editPrefill?.submittedData,
    msg?.metadata?.latest_session,
    msg?.metadata?.test_config_details,
    testConfiguration,
    submittedDataProp,
    isInternalAsset,
    sessionDetails?.continue_as_unauth,
    sessionDetails?.continueAsUnauth,
  ]);

  // ── Compute initialValues for ThrottlingSetup from metadata ──
  const throttlingInitialValues = useMemo(() => {
    if (editPrefill?.throttlingData?.vulnerability) {
      const v = editPrefill.throttlingData.vulnerability;
      const strat = editPrefill.throttlingData.strategy;
      const out = {
        rateLimit: v.rate_limit ?? 80,
        concurrency: v.concurrency ?? 10,
        requestTimeout: v.timeout ?? 5,
      };
      if (strat && ['stealth', 'standard', 'turbo', 'custom'].includes(String(strat).toLowerCase())) {
        out.testStrategy = String(strat).toLowerCase();
      }
      return out;
    }

    const configSettings =
      msg?.metadata?.test_config_details?.config_settings ||
      msg?.metadata?.latest_session?.test_config_settings ||
      msg?.metadata?.latest_session?.config_settings ||
      submittedData?.config_settings ||
      throttlingData;

    const previousThrottling = configSettings
      ? null
      : msg?.metadata?.test_config_details?.throttling ||
        msg?.metadata?.latest_session?.throttling ||
        submittedData?.throttling;

    if (configSettings?.vulnerability) {
      /**
       * Trust `configSettings.strategy` as the user-chosen preset name persisted by
       * `ThrottlingSetup.buildConfigSettings`. Without it, ThrottlingSetup falls back
       * to its `'standard'` default state, which silently mislabels Stealth/Turbo/Custom
       * runs on read-back even when the numeric sliders are correct.
       */
      const rawStrategy = String(configSettings.strategy ?? '').trim().toLowerCase();
      const out = {
        rateLimit: configSettings.vulnerability.rate_limit || 80,
        concurrency: configSettings.vulnerability.concurrency || 10,
        requestTimeout: configSettings.vulnerability.timeout || 5,
      };
      if (['stealth', 'standard', 'turbo', 'custom'].includes(rawStrategy)) {
        out.testStrategy = rawStrategy;
      }
      return out;
    }

    if (previousThrottling) {
      return {
        testStrategy: previousThrottling.testStrategy || 'standard',
        rateLimit: previousThrottling.rateLimit || 80,
        concurrency: previousThrottling.concurrency || 10,
        requestTimeout: previousThrottling.requestTimeout || 5,
      };
    }

    return {};
  }, [
    editPrefill?.throttlingData,
    msg?.metadata?.test_config_details?.config_settings,
    msg?.metadata?.latest_session?.test_config_settings,
    msg?.metadata?.latest_session?.config_settings,
    submittedData?.config_settings,
    throttlingData,
    msg?.metadata?.test_config_details?.throttling,
    msg?.metadata?.latest_session?.throttling,
    submittedData?.throttling,
  ]);

  // ── Also set submittedData for read-only display from metadata ──
  const hasSetSubmittedFromMeta = useRef(false);
  useEffect(() => {
    if (isReadOnly && !submittedData && !hasSetSubmittedFromMeta.current) {
      const latestSession = msg?.metadata?.latest_session;
      const testConfigDetails = msg?.metadata?.test_config_details;
      const scheduleFromMsg = msg?.metadata?.testConfig?.schedule;

      if (testConfigDetails) {
        setSubmittedData({
          testType: testConfigDetails.test_type,
          showAuthFields: testConfigDetails.authentication_type === 'authenticated',
          checked: testConfigDetails.authentication_type === 'authenticated',
          continueAsUnauth:
            testConfigDetails.continue_as_unauth ??
            testConfigDetails.continueAsUnauth ??
            sessionDetails?.continue_as_unauth ??
            sessionDetails?.continueAsUnauth,
          loginUrl: testConfigDetails.login_url,
          networkType: testConfigDetails.vpn_profile ? 'internal' : 'external',
          config_settings: testConfigDetails.config_settings,
          ...(scheduleFromMsg ? { schedule: scheduleFromMsg } : {}),
        });
        hasSetSubmittedFromMeta.current = true;
      } else if (latestSession) {
        setSubmittedData({
          testType: latestSession.test_type,
          networkType: latestSession.vpn_profile ? 'internal' : 'external',
          authenticated_test: latestSession.authenticated_test,
          showAuthFields: latestSession.authenticated_test,
          continueAsUnauth:
            latestSession.continue_as_unauth ??
            latestSession.continueAsUnauth ??
            sessionDetails?.continue_as_unauth ??
            sessionDetails?.continueAsUnauth ??
            (latestSession.authenticated_test !== undefined
              ? !latestSession.authenticated_test
              : true),
          loginUrl: latestSession.login_url,
          config_settings: latestSession.config_settings || latestSession.test_config_settings,
          ...(scheduleFromMsg ? { schedule: scheduleFromMsg } : {}),
        });
        hasSetSubmittedFromMeta.current = true;
      }
    }
  }, [
    isReadOnly,
    submittedData,
    msg?.metadata?.latest_session,
    msg?.metadata?.test_config_details,
    msg?.metadata?.testConfig?.schedule,
    sessionDetails?.continue_as_unauth,
    sessionDetails?.continueAsUnauth,
  ]);

  // ── Callbacks ──
  const handleTestSetupSubmit = (configData) => {
    setSubmittedData((prev) => {
      const preservedSchedule =
        prev?.schedule ?? editPrefill?.submittedData?.schedule ?? null;
      return {
        ...(prev || {}),
        ...configData,
        ...(preservedSchedule ? { schedule: preservedSchedule } : {}),
      };
    });
    setTimeout(() => {
      setActiveStep('throttling');
    }, 100);
  };

  const dispatchDeclaration = async (fullConfig) => {
    setSubmittedData(fullConfig);

    if (hideScheduleTab && typeof onPipelineThrottleSave === 'function') {
      setPipelineThrottleSaving(true);
      try {
        await onPipelineThrottleSave(fullConfig);
        setFormVisible(false);
        setDeclarationSubmitted(true);
      } catch {
        /* Caller surfaces errors (e.g. AlertContext); keep form editable */
      } finally {
        setPipelineThrottleSaving(false);
      }
      return;
    }

    /** Schedule GenPT modal: show declaration acknowledgment before POST (same copy as chat widget). */
    if (suppressDeclarationEvent && onConfigCompleted && embeddedInModal) {
      setPendingModalFullConfig(fullConfig);
      setDeclarationModalOpen(true);
      return;
    }

    /** Schedule GenPT modal without embedded chrome (legacy): complete immediately. */
    if (suppressDeclarationEvent && onConfigCompleted) {
      await onConfigCompleted(fullConfig);
      return;
    }

    setFormVisible(false);
    setDeclarationSubmitted(true);

    const declarationMessage = {
      type: MESSAGE_TYPES.DECLARATION_WIDGET,
      from: MESSAGE_SOURCES.SYSTEM,
      text: getMessageText({ type: MESSAGE_TYPES.DECLARATION_WIDGET }, finalTargetURL),
      timestamp: Date.now(),
      widget: {
        type: MESSAGE_TYPES.DECLARATION_WIDGET,
      },
      metadata: {
        testConfig: fullConfig,
        originalMessageType: msg?.type || null,
      },
    };

    if (!suppressDeclarationEvent) {
      window.dispatchEvent(
        new CustomEvent('genpt:addMessage', {
          detail: declarationMessage,
        })
      );
    }

    if (onConfigCompleted) {
      onConfigCompleted(fullConfig);
    }
  };

  const handleStartAndSchedule = (configSettings) => {
    setThrottlingData(configSettings);
    setSubmittedData((prev) => ({
      ...prev,
      config_settings: configSettings,
    }));
    if (hideScheduleTab) {
      setTimeout(() => {
        setSubmittedData((prev) => {
          void dispatchDeclaration({ ...prev, config_settings: configSettings });
          return prev;
        });
      }, 100);
    } else {
      setTimeout(() => {
        setActiveStep('schedule');
      }, 100);
    }
  };

  const handleDeclarationModalConfirm = async () => {
    if (!pendingModalFullConfig || !onConfigCompleted) return;
    setDeclarationModalLoading(true);
    try {
      await onConfigCompleted(pendingModalFullConfig);
      setFormVisible(false);
      setDeclarationSubmitted(true);
      setDeclarationModalOpen(false);
      setPendingModalFullConfig(null);
    } catch {
      /* ScheduleGenPTModal surfaces error popup; keep declaration open for retry */
    } finally {
      setDeclarationModalLoading(false);
    }
  };

  const handleDeclarationModalSetOpen = (next) => {
    if (declarationModalLoading) return;
    setDeclarationModalOpen(next);
    if (!next) setPendingModalFullConfig(null);
  };

  const handleDirectStartTest = (configSettings) => {
    const fullConfig = {
      ...submittedData,
      config_settings: configSettings,
    };
    dispatchDeclaration(fullConfig);
  };

  const handleScheduleSubmit = async (scheduleData) => {
    const fullConfig = {
      ...submittedData,
      config_settings: throttlingData,
      schedule: scheduleData,
    };
    try {
      await dispatchDeclaration(fullConfig);
    } catch {
      /* Schedule modal surfaces error popup; keep form editable */
    }
  };

  // Show waiting message if no targetURL (chat usage only)
  if (msg && !finalTargetURL) {
    return (
      <div className="rounded-lg border border-[#FFE0B2]/80 bg-[#FFF3E0] p-4">
        <p className="text-amber-950">Waiting for target URL...</p>
      </div>
    );
  }

  // Helper: determine step completion
  const isConfigDone = !!submittedData;
  /** In read-only, `throttlingData` is never set (wizard state); persisted `config_settings` counts as done. */
  const isThrottlingDone =
    !!throttlingData ||
    (isReadOnly &&
      !!(
        readOnlyAlwaysOpen ||
        submittedData?.config_settings ||
        submittedData?.throttling
      ));

  const getStepStyle = (stepName) => {
    const completed =
      declarationSubmitted ||
      isReadOnly ||
      (stepName === 'configuration' && isConfigDone && activeStep !== 'configuration') ||
      (stepName === 'throttling' && isThrottlingDone && activeStep !== 'throttling');
    const active = activeStep === stepName && !declarationSubmitted && !isReadOnly;

    let bgClass = 'bg-primary-200';
    let bgStyle = {};
    if (completed) {
      bgClass = '';
      bgStyle = { backgroundColor: '#3ADA7A' };
    } else if (active) {
      bgClass = 'bg-primary-500';
    }

    const labelBold =
      activeStep === stepName || completed || declarationSubmitted;

    /** Past / done steps: show label like a hyperlink (matches GenptConfigureModal). */
    const showLinkLabel = completed && activeStep !== stepName;

    return { bgClass, bgStyle, labelBold, showLinkLabel };
  };

  const getLineStyle = (fromDone) => {
    if (declarationSubmitted || isReadOnly || fromDone) {
      return { className: '', style: { backgroundColor: '#3ADA7A' } };
    }
    return { className: 'bg-primary-200', style: {} };
  };

  // Show the 3rd step whenever the user can still edit (modify setup): they must use
  // "Start & Schedule" on throttling to reach the schedule panel, but the stepper should
  // always show Configuration → Throttling → Schedule so scheduling is discoverable.
  // `readOnlyAlwaysOpen`: Activities “View Setup Details” — always show Schedule (wizard parity);
  // schedule UI may be defaults when no persisted schedule exists.
  // `hideScheduleTab`: pipeline CI/CD wizard — only Configuration → Throttling.
  const showScheduleStep =
    !hideScheduleTab &&
    (activeStep === 'schedule' ||
      (isReadOnly &&
        (readOnlyAlwaysOpen ||
          !!msg?.metadata?.testConfig?.schedule ||
          !!submittedData?.schedule)) ||
      (!isReadOnly && !declarationSubmitted));

  // ── Stepper UI ──
  const renderStepper = () => {
    const s1 = getStepStyle('configuration');
    const s2 = getStepStyle('throttling');
    const line1 = getLineStyle(isConfigDone);

    const stepDisc = embeddedInModal
      ? 'w-7 h-7 rounded-full flex items-center justify-center text-white font-semibold text-xs transition-colors relative z-10'
      : 'w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center text-white font-semibold transition-colors relative z-10';
    const lineTopClass = embeddedInModal ? 'top-3.5' : 'top-4 md:top-5';
    const labelClass = embeddedInModal
      ? 'mt-1.5 text-xs font-semibold whitespace-nowrap'
      : 'mt-2 text-sm font-semibold whitespace-nowrap';

    return (
      <div
        className={
          embeddedInModal
            ? 'mt-3 mb-3 flex justify-center rounded-xl bg-gray-50 px-4 py-3'
            : 'mt-4 mb-4 bg-gray-50 rounded-lg p-4 flex justify-center'
        }
      >
        <div className="relative flex items-start justify-between w-full md:w-[80%] min-w-0 px-2">
          {/* Configuration Step */}
          <div
            className="flex flex-col items-center cursor-pointer flex-shrink-0"
            onClick={() => setActiveStep('configuration')}
          >
            <div className={`${stepDisc} ${s1.bgClass}`} style={{ ...s1.bgStyle, opacity: 1 }}>
              1
            </div>
            <div
              className={`${labelClass} ${
                s1.showLinkLabel
                  ? 'text-primary-500 underline decoration-primary-500 underline-offset-2'
                  : s1.labelBold
                    ? 'text-black'
                    : 'text-gray-600'
              }`}
            >
              Configuration
            </div>
          </div>

          {/* Line 1: Configuration -> Throttling */}
          <div
            className={`absolute ${lineTopClass} h-0.5`}
            style={showScheduleStep ? { left: '10%', right: '57%' } : { left: '20%', right: '20%' }}
          >
            <div
              className={`h-full w-full transition-colors ${line1.className}`}
              style={line1.style}
            />
          </div>

          {/* Throttling Step */}
          <div
            className="flex flex-col items-center cursor-pointer flex-shrink-0"
            onClick={() => isConfigDone && setActiveStep('throttling')}
            style={{ cursor: isConfigDone ? 'pointer' : 'not-allowed' }}
          >
            <div className={`${stepDisc} ${s2.bgClass}`} style={{ ...s2.bgStyle, opacity: 1 }}>
              2
            </div>
            <div
              className={`${labelClass} ${
                s2.showLinkLabel
                  ? 'text-primary-500 underline decoration-primary-500 underline-offset-2'
                  : s2.labelBold
                    ? 'text-black'
                    : 'text-gray-600'
              }`}
            >
              Throttling
            </div>
          </div>

          {showScheduleStep && (() => {
            const s3 = getStepStyle('schedule');
            const line2 = getLineStyle(isThrottlingDone);
            return (
              <>
                {/* Line 2: Throttling -> Schedule */}
                <div className={`absolute ${lineTopClass} h-0.5`} style={{ left: '43%', right: '10%' }}>
                  <div
                    className={`h-full w-full transition-colors ${line2.className}`}
                    style={line2.style}
                  />
                </div>

                {/* Schedule Step */}
                <div
                  className="flex flex-col items-center cursor-pointer flex-shrink-0"
                  onClick={() => isThrottlingDone && setActiveStep('schedule')}
                  style={{ cursor: isThrottlingDone ? 'pointer' : 'not-allowed' }}
                >
                  <div
                    className={`${stepDisc} ${
                      embeddedInModal && declarationSubmitted
                        ? 'bg-white ring-2 ring-[#3ADA7A] ring-inset'
                        : s3.bgClass
                    }`}
                    style={
                      embeddedInModal && declarationSubmitted ? {} : { ...s3.bgStyle, opacity: 1 }
                    }
                  >
                    {embeddedInModal && declarationSubmitted ? (
                      <GreenCheck className="!w-5 !h-5 flex-shrink-0" aria-hidden />
                    ) : (
                      '3'
                    )}
                  </div>
                  <div
                    className={`${labelClass} ${
                      s3.showLinkLabel
                        ? 'text-primary-500 underline decoration-primary-500 underline-offset-2'
                        : s3.labelBold
                          ? 'text-black'
                          : 'text-gray-600'
                    }`}
                  >
                    Schedule
                  </div>
                </div>
              </>
            );
          })()}
        </div>
      </div>
    );
  };

  const rootChrome = embeddedInModal
    ? readOnlyUseModalHeader
      ? 'min-h-[64px] w-full rounded-lg border border-gray-200 bg-white px-2.5 py-2 sm:px-3 sm:py-2.5 text-left'
      : 'min-h-[64px] w-full rounded-lg border border-gray-200 bg-white p-4'
    : 'border border-[#EFEFEF] rounded-xl p-4 bg-white shadow-md transition-all duration-300 min-h-[64px] w-full md:w-[95%]';

  const scheduleStepSubmitLabel =
    !embeddedInModal || scheduleSubmitConfirmAndSave ? 'Confirm and Save' : 'SCHEDULE TEST';

  return (
    <>
    <div className={rootChrome}>
      {isReadOnly ? (
        <>
          {/* Collapsible Header for Read-Only Mode */}
          <div className="flex items-center justify-between w-full mb-2">
            {readOnlyUseModalHeader ? (
              <>
                <div className="flex items-center min-w-0 flex-1 text-lg font-semibold text-gray-800">
                  <HandymanIcon className="mr-2 text-primary-500 shrink-0" aria-hidden />
                  <span className="truncate">Test Setup</span>
                </div>
                <div className="flex items-center shrink-0 gap-0.5">
                  {!readOnlyAlwaysOpen && (
                    <button
                      type="button"
                      onClick={() => setExpanded(!expanded)}
                      className="p-2 hover:bg-gray-100 rounded-full transition-all text-gray-600"
                      aria-expanded={expanded}
                      aria-label={expanded ? 'Collapse test setup' : 'Expand test setup'}
                    >
                      {expanded ? (
                        <KeyboardArrowUpIcon sx={{ fontSize: 22 }} />
                      ) : (
                        <KeyboardArrowDownIcon sx={{ fontSize: 22 }} />
                      )}
                    </button>
                  )}
                  {typeof onReadOnlyModalClose === 'function' && (
                    <button
                      type="button"
                      onClick={onReadOnlyModalClose}
                      className="p-1.5 hover:bg-gray-100 rounded-full transition-all text-gray-600"
                      aria-label="Close"
                    >
                      <CloseIcon sx={{ fontSize: 20 }} />
                    </button>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3 min-w-0">
                  <GreenCheck />
                  <Typography variant="subtitle1" className="font-normal">
                    Test Setup
                  </Typography>
                </div>
                <button
                  type="button"
                  onClick={() => setExpanded(!expanded)}
                  className="p-2 shrink-0 hover:bg-gray-100 rounded-full transition-all"
                  aria-expanded={expanded}
                  aria-label={expanded ? 'Collapse test setup' : 'Expand test setup'}
                >
                  {expanded ? (
                    <ExpandLessIcon className="text-gray-600" />
                  ) : (
                    <ExpandMoreIcon className="text-gray-600" />
                  )}
                </button>
              </>
            )}
          </div>

          {(readOnlyAlwaysOpen || expanded) && (
            <>
              {renderStepper()}
              <div>
                {activeStep === 'configuration' ? (
                  <>
                    <div className="!mt-4 !mb-4 border-t-2 border-[#EFEFEF]" />
                    <TestSetup
                      initialValues={testSetupInitialValues}
                      targetURL={finalTargetURL}
                      assetId={msg?.metadata?.asset?.id}
                      validateTargetData={mergedValidateTargetData}
                      isInternalAsset={isInternalAsset}
                      mainUrl={mainUrl}
                      isReadOnly={true}
                      forScheduling={forScheduling}
                      assetType={assetType}
                      chatId={chatId}
                      nonce={msg?.metadata?.nonce}
                      serverAttemptsRemaining={msg?.metadata?.attempts_remaining}
                      scenarioSource={hideScheduleTab ? 'select' : 'define'}
                    />
                  </>
                ) : activeStep === 'throttling' ? (
                  <ThrottlingSetup
                    initialValues={throttlingInitialValues}
                    isReadOnly={true}
                  />
                ) : showScheduleStep ? (
                  <ScheduleTest
                    initialValues={submittedData?.schedule || {}}
                    isReadOnly={true}
                    submitButtonLabel={scheduleStepSubmitLabel}
                    assetId={msg?.metadata?.asset?.id ?? submittedData?.assetId ?? null}
                    excludeScheduleId={editingScheduleId}
                  />
                ) : null}
              </div>
            </>
          )}
        </>
      ) : (
        <>
          <div className="flex items-center text-lg font-semibold text-gray-800 mb-2">
            <HandymanIcon className="mr-2 text-primary-500" /> Test Setup
          </div>

          {renderStepper()}

          <div>
            {activeStep === 'configuration' ? (
              <TestSetup
                initialValues={testSetupInitialValues}
                targetURL={finalTargetURL}
                assetId={msg?.metadata?.asset?.id ?? submittedData?.assetId}
                validateTargetData={mergedValidateTargetData}
                isInternalAsset={isInternalAsset}
                mainUrl={mainUrl}
                isReadOnly={false}
                isDisabled={declarationSubmitted}
                onSubmit={handleTestSetupSubmit}
                forScheduling={forScheduling}
                assetType={assetType}
                chatId={chatId}
                nonce={msg?.metadata?.nonce}
                serverAttemptsRemaining={msg?.metadata?.attempts_remaining}
                scenarioSource={hideScheduleTab ? 'select' : 'define'}
              />
            ) : activeStep === 'throttling' ? (
              <ThrottlingSetup
                initialValues={throttlingInitialValues}
                isReadOnly={false}
                isDisabled={declarationSubmitted || pipelineThrottleSaving}
                onContinue={handleStartAndSchedule}
                onStartTest={embeddedInModal ? null : handleDirectStartTest}
                scheduleOnly={embeddedInModal}
                primaryBtnText={hideScheduleTab ? 'SAVE CONFIGURATION' : 'CONTINUE'}
              />
            ) : (
              <ScheduleTest
                initialValues={submittedData?.schedule || {}}
                isReadOnly={false}
                isDisabled={declarationSubmitted}
                onSubmit={handleScheduleSubmit}
                submitButtonLabel={scheduleStepSubmitLabel}
                assetId={msg?.metadata?.asset?.id ?? submittedData?.assetId ?? null}
                excludeScheduleId={editingScheduleId}
                lockStartDate={Boolean(editingScheduleId)}
              />
            )}
          </div>
        </>
      )}
    </div>
    <DeclarationModal
      open={declarationModalOpen}
      setOpen={handleDeclarationModalSetOpen}
      targetURL={finalTargetURL}
      onConfirm={handleDeclarationModalConfirm}
      isLoading={declarationModalLoading}
      onDiscardFlow={embeddedInModal && onDeclarationDiscard ? onDeclarationDiscard : undefined}
      confirmButtonLabel="CONFIRM"
    />
    </>
  );
}

export default TestConfigurationForm;
